import type {
    EntityPatch,
    HistoryDomainAdapter,
    HistoryEntitySnapshot,
    HistoryPatchApplyInput,
} from '..';
import {
    restoreAssemblyMateDocument,
    serializeAssemblyMateDocument,
    type AssemblyMateDocumentModel,
} from '../../assembly/document';
import {isSerializablePlainData} from './SerializablePlainData';

/**
 * 装配体 Mate 文档历史 adapter ID。
 */
export const ASSEMBLY_MATE_HISTORY_ADAPTER_ID = 'history-domain:assembly-mate-document';

/**
 * Mate 文档历史运行时存储。
 */
export interface AssemblyMateHistoryStore {
    /**
     * 恢复 Mate 文档快照。
     *
     * @param snapshot Mate 文档快照。
     */
    readonly restoreAssemblyMateDocumentSnapshot: (snapshot: AssemblyMateDocumentModel) => void;
}

/**
 * 创建 Mate 文档历史实体 ID。
 *
 * @param assemblyId 装配体 ID。
 * @returns Mate 文档历史实体 ID。
 */
export function createAssemblyMateDocumentHistoryEntityId(assemblyId: string): string {
    return `assembly-mate-document:${assemblyId}`;
}

/**
 * 归一化 Mate 文档历史快照。
 *
 * 设计说明：
 * Mate 文档必须以 cad-core document 层的序列化结果进入历史系统。这里不保存场景对象、
 * Vue 响应式对象或 BRep 运行时引用，只保留可重载的 Mate Definition plain data。
 *
 * @param snapshot Mate 文档快照。
 * @returns 可序列化 Mate 文档快照。
 */
function normalizeAssemblyMateHistorySnapshot(
    snapshot: AssemblyMateDocumentModel,
): AssemblyMateDocumentModel {
    return serializeAssemblyMateDocument(restoreAssemblyMateDocument(snapshot));
}

/**
 * 归一化 Mate 历史实体快照。
 *
 * @param snapshot 历史实体快照。
 * @returns 已归一化的历史实体快照。
 */
function normalizeAssemblyMateHistoryEntitySnapshot(
    snapshot: HistoryEntitySnapshot<AssemblyMateDocumentModel>,
): HistoryEntitySnapshot<AssemblyMateDocumentModel> {
    if (!snapshot.exists || !snapshot.data) {
        return snapshot;
    }

    return {
        ...snapshot,
        data: normalizeAssemblyMateHistorySnapshot(snapshot.data),
    };
}

/**
 * 判断 Mate 文档历史快照是否为 plain data。
 *
 * @param snapshot Mate 文档快照。
 * @returns 是否为可序列化 plain data。
 */
export function isPlainAssemblyMateHistorySnapshot(
    snapshot: AssemblyMateDocumentModel,
): boolean {
    return isSerializablePlainData(normalizeAssemblyMateHistorySnapshot(snapshot));
}

/**
 * 应用 Mate 文档历史快照。
 *
 * @param input 补丁应用输入。
 */
function applyAssemblyMateSnapshot(
    input: HistoryPatchApplyInput<AssemblyMateDocumentModel, AssemblyMateHistoryStore>,
): void {
    const snapshot = input.snapshot;

    if (!snapshot.exists || !snapshot.data) {
        throw new Error(`Mate 文档 ${input.patch.entityId} 缺少快照数据。`);
    }

    const normalizedSnapshot = normalizeAssemblyMateHistorySnapshot(snapshot.data);

    if (!isPlainAssemblyMateHistorySnapshot(normalizedSnapshot)) {
        throw new Error(`Mate 文档 ${input.patch.entityId} 的快照不是可序列化 plain data。`);
    }

    input.context.restoreAssemblyMateDocumentSnapshot(normalizedSnapshot);
}

/**
 * 比较 Mate 文档历史快照。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 是否语义相等。
 */
function areAssemblyMateSnapshotsEqual(
    left: HistoryEntitySnapshot<AssemblyMateDocumentModel>,
    right: HistoryEntitySnapshot<AssemblyMateDocumentModel>,
): boolean {
    return JSON.stringify(normalizeAssemblyMateHistoryEntitySnapshot(left))
        === JSON.stringify(normalizeAssemblyMateHistoryEntitySnapshot(right));
}

/**
 * 创建 Mate 文档历史 adapter。
 *
 * @returns Mate 文档历史 adapter。
 */
export function createAssemblyMateHistoryAdapter(): HistoryDomainAdapter<
    AssemblyMateDocumentModel,
    AssemblyMateHistoryStore
> {
    return {
        adapterId: ASSEMBLY_MATE_HISTORY_ADAPTER_ID,
        kind: 'mate',
        applySnapshot: applyAssemblyMateSnapshot,
        areSnapshotsEqual: areAssemblyMateSnapshotsEqual as HistoryDomainAdapter<
            AssemblyMateDocumentModel,
            AssemblyMateHistoryStore
        >['areSnapshotsEqual'],
    };
}

/**
 * 创建 Mate 文档历史补丁。
 *
 * @param patch 补丁字段。
 * @returns Mate 文档历史补丁。
 */
export function createAssemblyMateHistoryPatch(
    patch: Omit<EntityPatch<AssemblyMateDocumentModel>, 'adapterId' | 'entityKind'>,
): EntityPatch<AssemblyMateDocumentModel> {
    return {
        ...patch,
        before: normalizeAssemblyMateHistoryEntitySnapshot(patch.before),
        after: normalizeAssemblyMateHistoryEntitySnapshot(patch.after),
        entityKind: 'mate',
        adapterId: ASSEMBLY_MATE_HISTORY_ADAPTER_ID,
    };
}
